import {
  ApiResponse,
  BookmarksResponse,
  LikedProductsResponse,
  SavedProduct,
  LikedProduct,
  Product,
  PaginationMetadata
} from '../interfaces/bookmarks.interface';

/**
 * Process saved products API response
 */
export const processSavedProductsResponse = (response: ApiResponse<BookmarksResponse>) => {
  if (!response?.success || !response?.data) {
    throw new Error(response?.message || 'Failed to fetch saved products');
  }

  const savedProducts: SavedProduct[] = Array.isArray(response.data)
    ? response.data
    : Array.isArray(response.data.savedProducts)
      ? response.data.savedProducts
      : [];

  const pagination: PaginationMetadata =
    response.pagination ||
    (response.data as any)?.pagination ||
    createFallbackPagination(savedProducts.length);

  return {
    products: savedProducts.map(normalizeProduct),
    pagination,
  };
};

/**
 * Process liked products API response
 */
export const processLikedProductsResponse = (response: ApiResponse<LikedProductsResponse>) => {
  if (!response?.success || !response?.data) {
    throw new Error(response?.message || 'Failed to fetch liked products');
  }

  const likedProducts: LikedProduct[] = Array.isArray(response.data)
    ? response.data
    : Array.isArray(response.data.likedProducts)
      ? response.data.likedProducts
      : [];

  const pagination: PaginationMetadata =
    response.pagination ||
    (response.data as any)?.pagination ||
    createFallbackPagination(likedProducts.length);

  return {
    products: likedProducts.map(normalizeProduct),
    pagination,
  };
};

/**
 * Normalize a product to ensure consistent ID structure
 */
export const normalizeProduct = (product: Product | SavedProduct | LikedProduct): Product => {
  // Ensure we have a consistent ID field
  const id = product.id || product._id;

  if (!id) {
    throw new Error('Product must have an id or _id field');
  }

  // Return product with normalized ID
  return {
    ...product,
    id: id.toString()
  };
};

/**
 * Create fallback pagination metadata
 */
export const createFallbackPagination = (itemsLength: number, page: number = 1, limit: number = 15): PaginationMetadata => {
  return {
    total: itemsLength,
    page,
    limit,
    totalPages: itemsLength > 0 ? Math.ceil(itemsLength / limit) : 0,
  };
};

/**
 * Validate API response structure
 */
export const validateApiResponse = (response: any): response is ApiResponse => {
  return response && typeof response === 'object' && 'success' in response;
};
